import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

const SCOPE_DESCRIPTIONS: Record<string, string> = {
  'mcp:read':
    'Read access to your videos and transcripts (search, fetch metadata, fetch transcript text).',
};

export function OAuthConsentPage() {
  const navigate = useNavigate();
  const { user, isLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const [busy, setBusy] = useState<null | 'approve' | 'deny'>(null);
  const [error, setError] = useState<string | null>(null);

  const params = {
    client_id: searchParams.get('client_id') || '',
    client_name: searchParams.get('client_name') || 'An external application',
    redirect_uri: searchParams.get('redirect_uri') || '',
    code_challenge: searchParams.get('code_challenge') || '',
    code_challenge_method: searchParams.get('code_challenge_method') || '',
    scope: searchParams.get('scope') || 'mcp:read',
    state: searchParams.get('state') || '',
    resource: searchParams.get('resource') || '',
  };

  // If unauthenticated, send to login and come back here after.
  useEffect(() => {
    if (!isLoading && !user) {
      const nextUrl = `/oauth/consent?${searchParams.toString()}`;
      navigate(`/login?next=${encodeURIComponent(nextUrl)}`, { replace: true });
    }
  }, [user, isLoading, searchParams, navigate]);

  const decide = async (decision: 'approve' | 'deny') => {
    setError(null);
    setBusy(decision);
    try {
      const res = await api.post<{ data: { redirect_to: string } }>(
        '/oauth/authorize/approve',
        { ...params, decision }
      );
      // Hard redirect — the destination belongs to the OAuth client.
      window.location.href = res.data.redirect_to;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authorization failed');
      setBusy(null);
    }
  };

  if (isLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Basic validation up front so we don't render a useless form.
  const missing: string[] = [];
  if (!params.client_id) missing.push('client_id');
  if (!params.redirect_uri) missing.push('redirect_uri');
  if (!params.code_challenge) missing.push('code_challenge (PKCE required)');
  if (params.code_challenge_method !== 'S256') missing.push('code_challenge_method=S256');

  if (missing.length > 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow p-8">
          <h1 className="text-xl font-bold text-gray-900 mb-2">Invalid authorization request</h1>
          <p className="text-sm text-gray-600 mb-2">
            The request is missing required parameters and cannot proceed.
          </p>
          <ul className="text-sm text-gray-600 list-disc pl-5">
            {missing.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  let redirectHost = '(invalid)';
  try {
    redirectHost = new URL(params.redirect_uri).host;
  } catch {
    /* ignore */
  }

  const scopes = params.scope.split(/\s+/).filter(Boolean);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-xl p-8">
        <div className="flex items-center justify-center mb-6">
          <div className="h-12 w-12 bg-primary-600 rounded-xl flex items-center justify-center">
            <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
            </svg>
          </div>
        </div>

        <h1 className="text-xl font-bold text-gray-900 text-center mb-2">
          Authorize access to your transcripts
        </h1>
        <p className="text-sm text-gray-600 text-center mb-6">
          <span className="font-semibold">{params.client_name}</span> is requesting access to your
          SearchMyContent account.
        </p>

        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
            Permissions requested
          </h2>
          <ul className="space-y-2">
            {scopes.map((s) => (
              <li key={s} className="flex items-start gap-2 text-sm text-gray-700">
                <svg className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                </svg>
                <div>
                  <code className="text-xs font-mono text-gray-900">{s}</code>
                  <p className="text-xs text-gray-600 mt-0.5">
                    {SCOPE_DESCRIPTIONS[s] || 'Custom scope'}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
          <p className="text-xs text-amber-900">
            After approving, you'll be redirected to <span className="font-mono font-semibold">{redirectHost}</span>.
            Only continue if you initiated this from {params.client_name}.
          </p>
        </div>

        {error && (
          <div className="mb-4 rounded-lg bg-red-50 p-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={() => decide('deny')}
            disabled={busy !== null}
            className="btn-secondary flex-1"
          >
            {busy === 'deny' ? 'Cancelling…' : 'Deny'}
          </button>
          <button
            onClick={() => decide('approve')}
            disabled={busy !== null}
            className="btn-primary flex-1"
          >
            {busy === 'approve' ? 'Authorizing…' : 'Approve'}
          </button>
        </div>

        <p className="text-xs text-gray-500 text-center mt-6">
          Signed in as <span className="font-medium text-gray-700">{user.email}</span>
        </p>
      </div>
    </div>
  );
}
